import { actions, assign, createMachine, raise } from "xstate"
import * as F from "fp-ts/function"
import * as ROA from "fp-ts/ReadonlyArray"
import * as O from "fp-ts/Option"
import { match, P } from "ts-pattern"
import { ReceiptStateMachine, ReceiptTypes } from "../receipt-machine"
import { assertEvent } from "../../../../_common/xstate-helpers"
import { FareCard, FareCardIndex } from "../../read-models/fare-card"

type Context = ReceiptTypes["Context"]
type Events = ReceiptTypes["Events"]
type TypeState = ReceiptTypes["TypeState"]

const initialContext = { farePrices: {}, suitePrices: [], fareCards: [] }

export const receiptMachineV4 = (): ReceiptStateMachine => {
    return createMachine<Context, Events, TypeState>(
        {
            id: "receipt",
            initial: "empty",
            predictableActionArguments: true,
            context: initialContext,
            states: {
                empty: {
                    on: {
                        RECEIPT_FROM_AVAILABLE: "#receipt.present.priceFrom",
                    },
                },
                loading: {
                    on: {
                        RECEIPT_FROM_AVAILABLE: "#receipt.present.priceFrom",
                        RECEIPT_TOTAL_AVAILABLE: "#receipt.present.priceTotal",
                        GUESTS_CHANGED: { target: "#receipt.empty", actions: ["resetInitialContext"] },
                    },
                },
                present: {
                    states: {
                        priceFrom: {
                            on: {
                                GUESTS_CHANGED: { target: "#receipt.empty", actions: ["resetInitialContext"] },
                                SUITE_CATEGORY_UNSELECTED: {
                                    target: "#receipt.empty",
                                    actions: ["resetSelectedSuiteCategory"],
                                },
                                RECEIPT_TOTAL_AVAILABLE: "#receipt.present.priceTotal",
                            },
                        },
                        priceTotal: {
                            on: {
                                GUESTS_CHANGED: { target: "#receipt.empty", actions: ["resetInitialContext"] },
                                FARE_SUITE_PRICES_LOADING: { target: "#receipt.loading", cond: "isSelectedFare" },
                                FARE_UNSELECTED: {
                                    target: "priceFrom",
                                    actions: ["resetSelectedFare", "updateReceipt"],
                                },
                            },
                        },
                    },
                },
            },
            on: {
                FARE_CARD_PRICE_CHANGED: { actions: ["setFareCard", "updateReceipt"] },

                SUITE_CATEGORY_SELECTED: { actions: ["setSelectedSuiteCategory", "updateReceipt"] },
                //TODO - v4: review events for quote if needed (SELECT_QUOTE_SUITE_CATEGORY)
                FARE_SELECTED: { actions: ["setSelectedFare", "updateReceipt"] },

                CHECKOUT_VALIDATED: { actions: ["updateSuitePaymentReceipt"] },
                CHECKOUT_STARTED: { actions: ["updateSuitePaymentReceipt"] },
            },
        },
        {
            guards: {
                isSelectedFare: (ctx, ev) => {
                    assertEvent(ev, "FARE_SUITE_PRICES_LOADING")
                    return ev.index == ctx.selectedFare?.index
                },
            },
            actions: {
                setFareCard: actions.assign<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "FARE_CARD_PRICE_CHANGED")
                    const newFareCards = F.pipe(
                        ctx.fareCards,
                        ROA.filter((x) => x.index !== ev.fareCard.index),
                        ROA.append(ev.fareCard),
                    )
                    return { fareCards: newFareCards }
                }),
                setSelectedSuiteCategory: actions.assign<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "SUITE_CATEGORY_SELECTED")
                    return { selectedSuiteCategory: ev.suiteCategory }
                }),
                setSelectedFare: actions.assign<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "FARE_SELECTED")
                    return { selectedFare: ev.selectedFare }
                }),
                updateReceipt: actions.pure<Context, Events>((ctx, ev) => {
                    const fare = ctx.selectedFare?.index
                    const suiteCategory = ctx.selectedSuiteCategory

                    return match({ suiteCategory, fare })
                        .with({ suiteCategory: P.string, fare: fareIndex }, ({ fare }) => receiptPriceTotal(ctx, fare))
                        .with({ suiteCategory: P.string, fare: P.nullish }, () => receiptPriceFrom(ctx))
                        .with({ suiteCategory: P.nullish, fare: fareIndex }, () => [])
                        .with({ suiteCategory: P.nullish, fare: P.nullish }, () => [])
                        .exhaustive()
                }),
                updateSuitePaymentReceipt: actions.pure<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "CHECKOUT_VALIDATED", "CHECKOUT_STARTED")

                    const receipt = ctx.receipt
                    if (!receipt || receipt.type === "price-from") return []

                    return [
                        assign<Context, Events>({
                            receipt: { ...receipt, suitePayment: ev.response.validatedPrice.suitePayment },
                        }),
                    ]
                }),
                resetInitialContext: assign<Context, Events>(() => ({
                    ...initialContext,
                    selectedFare: undefined,
                    selectedSuiteCategory: undefined,
                    receipt: undefined,
                })),
                resetSelectedSuiteCategory: assign<Context, Events>(() => ({
                    selectedSuiteCategory: undefined,
                })),
                resetSelectedFare: assign<Context, Events>(() => ({
                    selectedFare: undefined,
                })),
                logMe: actions.log<Context, Events>((ctx, ev) => ({ type: ev.type, ctx, ev })),
            },
        },
    )
}

const receiptPriceFrom = (ctx: Context) => {
    const cheapestFarePrice = FareCard.cheapest(ctx.fareCards)
    if (O.isNone(cheapestFarePrice)) return []
    return [
        assign<Context, Events>({
            receipt: { type: "price-from", price: cheapestFarePrice.value.response.price },
        }),
        raise<Context, Events>({ type: "RECEIPT_FROM_AVAILABLE" }),
    ]
}

const receiptPriceTotal = (ctx: Context, selectedFare: FareCardIndex) => {
    const price = F.pipe(
        ctx.fareCards,
        ROA.filter(FareCard.hasStatus("good")),
        ROA.findFirst((x) => x.index === selectedFare),
        O.map((x) => x.response),
    )
    if (O.isNone(price)) return []

    return [
        assign<Context, Events>({
            receipt: {
                type: "price-total",
                price: price.value.price,
                suitePayment: price.value.suitePayment,
            },
        }),
        raise<Context, Events>({ type: "RECEIPT_TOTAL_AVAILABLE" }),
    ]
}

const fareIndex = P.when((x) => FareCardIndex.is(x))
